import React, { FC } from 'react';
import styled from 'styled-components';
import { BellOutlined } from '@ant-design/icons';
import {
  FEE_NOTICE_ANCHOR_ID,
  FEE_NOTICE_CELLS,
  FEE_NOTICE_CONTACT_HREF,
  FEE_NOTICE_EFFECTIVE_DATE,
  FEE_NOTICE_FOOTNOTE_FEE,
  FEE_NOTICE_FOOTNOTE_MHTP,
  FEE_NOTICE_HEADING_ID,
  FEE_NOTICE_QUALIFIER,
} from '../../constants/feeChangeNotice';

const BandSection = styled.section`
  margin: 22px 0 28px;
  padding: 24px;
  background: #e9eef3;
  border: 1px solid #d8e0e8;
  border-radius: 12px;
  text-align: left;
  font-family: 'Euclid Circular A', sans-serif;
  color: #1a202c;

  @media screen and (max-width: 480px) {
    padding: 18px;
  }
`;

const BandHeader = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px 10px;
  margin-bottom: 18px;

  .anticon {
    font-size: 17px;
    color: #1a202c;
  }
`;

const BandHeading = styled.h2`
  margin: 0;
  font-size: 17px;
  font-weight: 600;
  line-height: 24px;
  color: #1a202c;
`;

const EffectivePill = styled.span`
  display: inline-block;
  padding: 4px 12px;
  border-radius: 999px;
  background: #007ac0;
  color: #ffffff;
  font-size: 13px;
  font-weight: 600;
  line-height: 18px;
  white-space: nowrap;

  @media screen and (max-width: 480px) {
    flex-basis: 100%;
  }
`;

const CellGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;

  @media screen and (max-width: 860px) {
    grid-template-columns: repeat(1, minmax(0, 1fr));
  }
`;

const Cell = styled.div`
  background: #f5f7f9;
  border: 1px solid #e3e9ee;
  border-radius: 8px;
  padding: 18px;
`;

const CellTitle = styled.p`
  margin: 0;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: #5b5f66;
`;

const CellPrice = styled.p`
  margin: 8px 0 10px;
  font-size: 30px;
  font-weight: 700;
  line-height: 36px;
  color: #1a202c;
`;

const Marker = styled.sup`
  font-size: 0.45em;
  font-weight: 600;
  top: -0.9em;
  margin-left: 2px;
`;

const CellOutOfPocket = styled.p`
  margin: 0;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: #1a202c;
`;

const OutOfPocketAmount = styled.span`
  font-weight: 600;
  color: #007ac0;
`;

const Qualifier = styled.p`
  margin: 18px 0 0;
  font-size: 14px;
  font-weight: 400;
  line-height: 22px;
  color: #5b5f66;
`;

const Footnote = styled.p`
  margin: 14px 0 0;
  font-size: 13px;
  font-weight: 400;
  line-height: 20px;
  color: #5b5f66;
`;

const Support = styled.p`
  margin: 14px 0 0;
  font-size: 14px;
  line-height: 22px;
  color: #1a202c;

  a {
    color: #007ac0;
    font-weight: 600;

    &:focus-visible {
      outline: 2px solid #007ac0;
      outline-offset: 2px;
      border-radius: 2px;
    }
  }
`;

const FeeChangeBand: FC = () => (
  <BandSection id={FEE_NOTICE_ANCHOR_ID} aria-labelledby={FEE_NOTICE_HEADING_ID}>
    <BandHeader>
      <BellOutlined aria-hidden="true" />
      <BandHeading id={FEE_NOTICE_HEADING_ID}>New session fees</BandHeading>
      <EffectivePill>{`Effective ${FEE_NOTICE_EFFECTIVE_DATE}`}</EffectivePill>
    </BandHeader>

    <CellGrid>
      {FEE_NOTICE_CELLS.map((cell) => (
        <Cell key={cell.id}>
          <CellTitle>{`${cell.title} — ${cell.service}`}</CellTitle>
          <CellPrice>
            {cell.fee}
            <Marker aria-hidden="true">**</Marker>
          </CellPrice>
          <CellOutOfPocket>
            <OutOfPocketAmount>{`Est. out of pocket ${cell.outOfPocket}`}</OutOfPocketAmount>
            {' with a Mental Health Treatment Plan'}
            <Marker aria-hidden="true">*</Marker>
          </CellOutOfPocket>
        </Cell>
      ))}
    </CellGrid>

    <Qualifier>{FEE_NOTICE_QUALIFIER}</Qualifier>

    <Footnote>{`* ${FEE_NOTICE_FOOTNOTE_MHTP}`}</Footnote>
    <Footnote>{`** ${FEE_NOTICE_FOOTNOTE_FEE}`}</Footnote>

    <Support>
      Have a question?
      {' '}
      <a href={FEE_NOTICE_CONTACT_HREF} target="_blank" rel="noreferrer">
        We&apos;re here to help.
      </a>
    </Support>
  </BandSection>
);

export default FeeChangeBand;
